import React, { useCallback, useMemo, useState } from 'react';
import { Form, Formik, FormikHelpers, Field } from 'formik';
import cn from 'classnames';
import styles from './ForgotPassword.module.css';

import { resetPassword } from '@round/api';
import FormField from '../../../../ui/DataEntry/FormField/FormField';
import Label from '../../../../ui/DataEntry/Label/Label';
import Button from '../../../../ui/Buttons/Button/Button';
import { Helmet } from 'react-helmet';

type FormValues = { email: string };

const initialValues: FormValues = { email: '' };

const ForgotPassword = () => {
    const [letterWasSent, setLetterWasSent] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const copy = useMemo(() => {
        if (letterWasSent) {
            return 'An email with a link to set a new password has been sent to the email address you have entered';
        }

        if (errorMessage) {
            return errorMessage;
        }

        return `If you have forgotten your password, please enter your account's email address above
                and click the "Reset my password" button.
                You will receive an email that contains a link to set a new password.`;
    }, [errorMessage, letterWasSent]);

    const onSubmit = useCallback(async ({ email }: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
        try {
            setSubmitting(true);
            await resetPassword(email);
            setErrorMessage('');
            setLetterWasSent(true);
        } catch (e) {
            setErrorMessage((e as Error).message);
        } finally {
            setSubmitting(false);
        }
    }, []);

    return (
        <>
            <Helmet title="Forgot Password | trnds" />
            <div className={styles.container}>
                <h1 className={styles.title}>Forgot password</h1>

                <Formik initialValues={initialValues} onSubmit={onSubmit}>
                    {({ submitForm, isSubmitting }) => (
                        <Form className={styles.form}>
                            <FormField>
                                <Label htmlFor="email">Email</Label>
                                <Field id="email" name="email" placeholder="Enter your email" />
                            </FormField>

                            <Button
                                className={styles.submit}
                                loading={isSubmitting}
                                disabled={letterWasSent}
                                type="filled"
                                color="black"
                                onClick={submitForm}
                            >
                                Reset my password
                            </Button>
                            <p className={cn(styles.copy, { [styles.error]: !!errorMessage })}>{copy}</p>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    );
};

export default ForgotPassword;
